import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Compass, ArrowRight, Sparkles, CalendarClock, MapPin, ChevronDown, ChevronUp } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLLAPSE_KEY = 'fp_cdp_hero_collapsed';

/**
 * NC CDP hero card, the "This is where you started" moment.
 *
 * On first render the card asks the student whether they want to keep it big or
 * shrink it to a slim strip. Their choice persists in localStorage so a repeat
 * visit doesn't nag them. Collapsed state renders a one-line chip you can click
 * to re-expand.
 */
export default function NCCDPHeroCard() {
  const { getAuthHeader } = useAuth();
  const [cdp, setCdp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSE_KEY) === '1'; } catch { return false; }
  });
  const [askedShrink, setAskedShrink] = useState(() => {
    try { return !!localStorage.getItem(COLLAPSE_KEY); } catch { return true; }
  });

  const persistCollapsed = (val) => {
    setCollapsed(val);
    setAskedShrink(true);
    try { localStorage.setItem(COLLAPSE_KEY, val ? '1' : '0'); } catch { /* ignore */ }
  };

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/nc/cdp/initial-direction`, getAuthHeader())
      .then((r) => { if (alive) setCdp(r.data); })
      .catch(() => { if (alive) setCdp(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [getAuthHeader]);

  if (loading || !cdp?.has_initial_direction) return null;

  const { initial, current_grade, next_required_review_grade, last_reviewed_grade, status } = cdp;
  const createdYear = initial.created_at ? new Date(initial.created_at).getFullYear() : null;
  const reviewDue = status === 'review_due';

  // Collapsed strip, one-line chip that opens the plan on click
  if (collapsed) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        data-testid="nc-cdp-hero-card-collapsed"
        className="relative overflow-hidden rounded-full border border-cyan-500/25 bg-[#0a1729]/70 px-4 py-2 mb-4
          flex items-center gap-3 hover:border-cyan-300/50 transition-colors"
        style={{ boxShadow: '0 20px 60px -50px rgba(34,211,238,0.5)' }}
      >
        <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-cyan-200">
          <Compass className="h-3 w-3" /> My CDP
        </span>
        <span className="text-sm text-slate-200 truncate">
          {ordinal(current_grade || initial.created_grade)} grade →{' '}
          <span className="font-semibold text-white">{initial.career_interest}</span>
        </span>
        {reviewDue && (
          <span className="rounded-full border border-amber-300/40 bg-amber-400/10 px-2 py-0.5 text-[10px] uppercase tracking-wider text-amber-200">
            Review due
          </span>
        )}
        <button
          onClick={() => persistCollapsed(false)}
          data-testid="cdp-expand"
          className="ml-auto inline-flex items-center gap-1 text-xs text-slate-300 hover:text-cyan-200 transition-colors"
        >
          <ChevronDown className="h-3.5 w-3.5" /> Expand
        </button>
        <Link
          to="/app/nc-cdp"
          data-testid="cdp-open-plan-collapsed"
          className="inline-flex items-center gap-1 text-xs text-cyan-200 hover:text-white transition-colors"
        >
          Open plan <ArrowRight className="h-3 w-3" />
        </Link>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      data-testid="nc-cdp-hero-card"
      className="relative overflow-hidden rounded-2xl border border-cyan-500/25 bg-[#0a1729]/70 p-6 md:p-7 mb-6"
      style={{ boxShadow: '0 40px 100px -60px rgba(34,211,238,0.55)' }}
    >
      {/* glow accents */}
      <div aria-hidden className="pointer-events-none absolute -left-32 -top-24 h-72 w-72 rounded-full blur-3xl opacity-30"
        style={{ background: 'radial-gradient(circle, rgba(34,211,238,0.35), transparent 65%)' }} />
      <div aria-hidden className="pointer-events-none absolute -bottom-32 -right-24 h-72 w-72 rounded-full blur-3xl opacity-25"
        style={{ background: 'radial-gradient(circle, rgba(167,139,250,0.35), transparent 65%)' }} />

      {/* Minimize control */}
      <button
        onClick={() => persistCollapsed(true)}
        data-testid="cdp-minimize"
        title="Shrink this card"
        className="absolute right-4 top-4 z-10 inline-flex items-center gap-1 rounded-full border border-white/15 bg-white/[0.03] px-2 py-1 text-[10px] uppercase tracking-wider text-slate-300 hover:text-white hover:border-cyan-300/40 transition-colors"
      >
        <ChevronUp className="h-3 w-3" /> Shrink
      </button>

      <div className="relative flex flex-wrap items-start justify-between gap-6">
        <div className="flex-1 min-w-[280px]">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-cyan-300/40 bg-cyan-400/10 px-2.5 py-0.5
              text-[10px] font-semibold uppercase tracking-[0.2em] text-cyan-200">
              <Compass className="h-3 w-3" /> My NC Career Development Plan
            </span>
            {reviewDue ? (
              <span className="inline-flex items-center gap-1 rounded-full border border-amber-300/40 bg-amber-400/10 px-2 py-0.5
                text-[10px] font-semibold uppercase tracking-wider text-amber-200"
                data-testid="cdp-status-badge">
                <CalendarClock className="h-3 w-3" /> Review due
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full border border-cyan-300/40 bg-cyan-400/10 px-2 py-0.5
                text-[10px] font-semibold uppercase tracking-wider text-cyan-200"
                data-testid="cdp-status-badge">
                Current
              </span>
            )}
          </div>

          <h2 className="font-outfit mt-3 text-2xl sm:text-3xl font-semibold text-white tracking-tight">
            You entered {current_grade ? `${ordinal(current_grade)} grade` : 'high school'} interested in{' '}
            <span className="bg-gradient-to-r from-cyan-300 via-sky-300 to-violet-400 bg-clip-text text-transparent">
              {initial.career_interest}
            </span>
          </h2>

          <p className="mt-2 max-w-2xl text-sm text-slate-300/90 leading-relaxed" data-testid="cdp-framing">
            This is the direction you entered high school with. FuturePath will help you
            explore, test, and refine it as you learn more about yourself.
          </p>

          <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl" data-testid="cdp-facts">
            <Fact
              icon={MapPin}
              label="Career Cluster"
              value={shortCluster(initial.cluster)}
              hue="#22d3ee"
            />
            <Fact
              icon={ArrowRight}
              label="Postsecondary"
              value={initial.postsecondary_direction}
              hue="#a78bfa"
            />
            <Fact
              icon={Sparkles}
              label="Created"
              value={createdYear ? `${ordinal(initial.created_grade)} · ${createdYear}` : `${ordinal(initial.created_grade)} grade`}
              hue="#38bdf8"
            />
            <Fact
              icon={CalendarClock}
              label={reviewDue ? 'Review due' : 'Last reviewed'}
              value={
                reviewDue
                  ? next_required_review_grade ? `${ordinal(next_required_review_grade)} grade` : 'Now'
                  : last_reviewed_grade ? `${ordinal(last_reviewed_grade)} grade` : 'Never'
              }
              hue={reviewDue ? '#fbbf24' : '#22d3ee'}
            />
          </div>

          {(initial.top_interests?.length > 0 || initial.strengths?.length > 0) && (
            <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs">
              {initial.top_interests?.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-slate-300">Interests:</span>
                  {initial.top_interests.slice(0, 4).map((t) => (
                    <span key={t} className="rounded-full border border-cyan-300/25 bg-cyan-400/[0.06] px-2 py-0.5 text-cyan-100">
                      {t}
                    </span>
                  ))}
                </div>
              )}
              {initial.strengths?.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-slate-300">Strengths:</span>
                  {initial.strengths.slice(0, 3).map((s) => (
                    <span key={s} className="rounded-full border border-violet-300/25 bg-violet-400/[0.06] px-2 py-0.5 text-violet-100">
                      {s}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-col items-stretch gap-2">
          <Link
            to="/app/nc-cdp"
            data-testid="cdp-open-plan"
            className="inline-flex items-center justify-center gap-1.5 rounded-full px-5 py-2.5
              bg-gradient-to-r from-cyan-500 to-violet-500 text-white text-sm font-semibold
              hover:opacity-95 transition-opacity shadow-[0_0_30px_-8px_rgba(34,211,238,0.6)]"
          >
            Open my plan <ArrowRight className="h-4 w-4" />
          </Link>
          <Link
            to="/app/calculator"
            data-testid="cdp-explore-life"
            className="inline-flex items-center justify-center gap-1.5 rounded-full px-5 py-2.5
              border border-slate-600/60 text-slate-200 text-sm hover:border-cyan-300/50 hover:text-cyan-100 transition-colors"
          >
            Design my life
          </Link>
        </div>
      </div>

      {/* First-visit shrink prompt, asks the student whether to keep it big.
          Only appears once, then remembered forever in localStorage. */}
      <AnimatePresence>
        {!askedShrink && (
          <motion.div
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 6 }}
            className="relative mt-5 flex flex-wrap items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5"
            data-testid="cdp-shrink-prompt"
          >
            <p className="text-xs text-slate-300">
              Want to shrink this so it takes less room? You can always expand it again.
            </p>
            <button
              onClick={() => persistCollapsed(true)}
              data-testid="cdp-shrink-yes"
              className="ml-auto inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs text-white bg-gradient-to-r from-cyan-500 to-violet-500 hover:opacity-95 transition-opacity"
              style={{ boxShadow: '0 0 22px -8px rgba(34,211,238,0.6)' }}
            >
              Yes, shrink it
            </button>
            <button
              onClick={() => persistCollapsed(false)}
              data-testid="cdp-shrink-no"
              className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs border border-slate-600/60 text-slate-200 hover:border-cyan-300/40 hover:text-white transition-colors"
            >
              Keep it big
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function Fact({ icon: Icon, label, value, hue }) {
  return (
    <div
      className="rounded-xl border p-3"
      style={{ borderColor: `${hue}33`, background: `${hue}0a` }}
    >
      <div className="flex items-center gap-1.5">
        <Icon className="h-3 w-3" style={{ color: hue }} />
        <p className="text-[10px] uppercase tracking-wider text-slate-300">{label}</p>
      </div>
      <p className="mt-1 text-sm font-semibold text-white truncate">{value || '-'}</p>
    </div>
  );
}

function ordinal(n) {
  if (n == null) return '';
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
}

function shortCluster(c) {
  if (!c) return '-';
  // "Science, Technology, Engineering & Mathematics" → "STEM"
  const map = {
    'Science, Technology, Engineering & Mathematics': 'STEM',
    'Health Science': 'Health Science',
    'Information Technology': 'Info Tech',
  };
  return map[c] || c;
}
